import { useTranslation } from "@redotech/redo-i18n";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { memo } from "react";
import * as progressCss from "./walkthrough-progress-bar.module.css";

export function computeActiveProgressSegments({
  stepIndex,
  totalSteps,
}: {
  stepIndex: number;
  totalSteps: number;
}): number {
  if (totalSteps <= 0) {
    return 0;
  }
  return Math.min(totalSteps, Math.max(0, stepIndex + 1));
}

export const WalkthroughProgressBar = memo(function WalkthroughProgressBar({
  stepIndex,
  totalSteps,
  dismiss,
}: {
  stepIndex: number;
  totalSteps: number;
  dismiss(): void;
}) {
  const { t } = useTranslation();
  const activeSegments = computeActiveProgressSegments({
    stepIndex,
    totalSteps,
  });
  const progressLabel =
    totalSteps > 0
      ? t("Walkthrough progress: step {{step}} of {{totalSteps}}", {
          step: activeSegments,
          totalSteps,
        })
      : t("Walkthrough progress");

  return (
    <Flex
      align="center"
      className={progressCss.container}
      data-testid="walkthrough-progress-bar"
      gap="2xl"
    >
      <div
        aria-label={progressLabel}
        aria-valuemax={totalSteps}
        aria-valuemin={0}
        aria-valuenow={activeSegments}
        className={progressCss.track}
        role="progressbar"
      >
        {Array.from({ length: totalSteps }, (_, index) => (
          <div
            className={
              index < activeSegments
                ? `${progressCss.segment} ${progressCss.segmentActive}`
                : progressCss.segment
            }
            key={index}
          />
        ))}
      </div>
      <RedoButton
        hierarchy="secondary"
        onClick={dismiss}
        size="sm"
        text={t("Exit walkthrough")}
      />
    </Flex>
  );
});
